import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent, MouseEvent } from 'react'
import type { FormTemplateField, FormTemplateSection } from '@/types'
import { PlusIcon, Bars4Icon } from '@heroicons/react/24/outline'
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { filterFieldsByKeyword, normalizeKeyword } from './fieldFilters'

interface FieldCanvasProps {
  section?: FormTemplateSection
  fields: FormTemplateField[]
  selectedFieldId?: string
  onSelectField: (fieldId: string) => void
  onAddField: (sectionId: string) => void
  onRenameField: (fieldId: string, label: string) => Promise<void> | void
  onReorderFields: (sectionId: string, orderedIds: string[]) => void
  filterKeyword?: string
}

const fieldTypeLabels: Partial<Record<FormTemplateField['type'], string>> = {
  SHORT_TEXT: '短文字',
  LONG_TEXT: '長文字',
  RICH_TEXT: '富文本',
  INTEGER: '整數',
  DECIMAL: '小數',
  PERCENTAGE: '百分比',
  CURRENCY: '貨幣',
  SINGLE_SELECT: '單選',
  MULTI_SELECT: '多選',
  DROPDOWN: '下拉選單',
  RATING: '評分',
  DATE: '日期',
  TIME: '時間',
  DATETIME: '日期時間',
  DATE_RANGE: '日期區間',
  BOOLEAN: '是非題',
  CHECKBOX: '勾選',
  ATTACHMENT: '附件',
  IMAGE: '圖片',
  VIDEO: '影片',
  SIGNATURE: '簽名',
  QR_CODE: 'QR Code',
  BARCODE: '條碼',
  RANGE: '量測範圍',
  SCALE: '量尺',
  PARAGRAPH: '段落說明',
}

export const FieldCanvas = ({
  section,
  fields,
  selectedFieldId,
  onSelectField,
  onAddField,
  onRenameField,
  onReorderFields,
  filterKeyword,
}: FieldCanvasProps) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null)
  const [draftLabel, setDraftLabel] = useState('')
  const [originalLabel, setOriginalLabel] = useState('')
  const [savingFieldId, setSavingFieldId] = useState<string | null>(null)

  const sectionId = section?.id ?? null

  const keyword = normalizeKeyword(filterKeyword ?? '')
  const isFiltering = keyword.length > 0

  const sortedFields = useMemo(() => {
    if (!sectionId) {
      return [] as FormTemplateField[]
    }
    return filterFieldsByKeyword(fields, sectionId, keyword)
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  }, [fields, keyword, sectionId])

  const fieldIds = useMemo(() => sortedFields.map(field => field.id), [sortedFields])

  useEffect(() => {
    if (editingFieldId && !fieldIds.includes(editingFieldId)) {
      setEditingFieldId(null)
      setDraftLabel('')
      setOriginalLabel('')
    }
  }, [editingFieldId, fieldIds])

  const handleSelectField = useCallback((fieldId: string) => {
    onSelectField(fieldId)
    if (editingFieldId && editingFieldId !== fieldId) {
      setEditingFieldId(null)
      setDraftLabel('')
      setOriginalLabel('')
    }
  }, [editingFieldId, onSelectField])

  const beginEditingField = useCallback((field: FormTemplateField) => {
    onSelectField(field.id)
    setEditingFieldId(field.id)
    setDraftLabel(field.label ?? '')
    setOriginalLabel(field.label ?? '')
  }, [onSelectField])

  const cancelEditingField = useCallback(() => {
    setEditingFieldId(null)
    setDraftLabel('')
    setOriginalLabel('')
  }, [])

  const commitFieldLabel = useCallback(async (field: FormTemplateField) => {
    if (editingFieldId !== field.id) {
      return
    }
    if (savingFieldId === field.id) {
      return
    }
    const trimmed = draftLabel.trim()
    const baseline = originalLabel.trim()
    if (!trimmed) {
      setEditingFieldId(null)
      setDraftLabel('')
      setOriginalLabel('')
      return
    }
    if (trimmed === baseline) {
      setEditingFieldId(null)
      setDraftLabel('')
      setOriginalLabel('')
      return
    }
    try {
      setSavingFieldId(field.id)
      await onRenameField(field.id, trimmed)
      setEditingFieldId(null)
      setDraftLabel('')
      setOriginalLabel('')
    } catch (error) {
      console.error('[FieldCanvas] Failed to rename field', error)
    } finally {
      setSavingFieldId(null)
    }
  }, [draftLabel, editingFieldId, onRenameField, originalLabel, savingFieldId])

  const handleDragEnd = (event: DragEndEvent) => {
    if (isFiltering) return
    if (!sectionId) return
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = fieldIds.indexOf(active.id as string)
    const newIndex = fieldIds.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    const reordered = arrayMove(fieldIds, oldIndex, newIndex)
    onReorderFields(sectionId, reordered)
  }

  if (!sectionId || !section) {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white text-sm text-gray-500">
        <div className="text-center">
          <Bars4Icon className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p>請先選取左側節點以檢視欄位</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">{section.title}</h3>
          {section.description && (
            <p className="text-xs text-gray-500 mt-0.5">{section.description}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onAddField(section.id)}
          className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          <PlusIcon className="h-4 w-4 mr-1" /> 新增欄位
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {sortedFields.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 py-12 text-sm text-gray-500">
            {isFiltering ? '沒有符合搜尋條件的欄位' : '尚未為此節點設定欄位'}
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {sortedFields.map((field) => (
                  <SortableFieldItem
                    key={field.id}
                    field={field}
                    isActive={field.id === selectedFieldId}
                    isEditing={editingFieldId === field.id}
                    draftLabel={
                      editingFieldId === field.id
                        ? draftLabel
                        : field.label ?? ''
                    }
                    onSelect={() => handleSelectField(field.id)}
                    onStartEditing={() => beginEditingField(field)}
                    onChangeDraft={setDraftLabel}
                    onCommit={() => commitFieldLabel(field)}
                    onCancel={cancelEditingField}
                    disableDrag={isFiltering || editingFieldId === field.id}
                    saving={savingFieldId === field.id}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
        {isFiltering && (
          <p className="mt-2 text-xs text-gray-400">搜尋模式下暫停拖曳排序</p>
        )}
      </div>
    </div>
  )
}

const SortableFieldItem = ({
  field,
  isActive,
  isEditing,
  draftLabel,
  onSelect,
  onStartEditing,
  onChangeDraft,
  onCommit,
  onCancel,
  disableDrag = false,
  saving = false,
}: {
  field: FormTemplateField
  isActive: boolean
  onSelect: () => void
  isEditing: boolean
  draftLabel: string
  onStartEditing: () => void
  onChangeDraft: (value: string) => void
  onCommit: () => void
  onCancel: () => void
  disableDrag?: boolean
  saving?: boolean
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const dragHandleProps = disableDrag ? {} : { ...attributes, ...listeners }
  const dragHandleClass = disableDrag
    ? 'cursor-default text-gray-300'
    : 'cursor-grab text-gray-400'

  const label = field.label || '未命名欄位'

  const handleContainerClick = () => {
    if (!isEditing) {
      onSelect()
    }
  }

  const handleContainerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleContainerClick()
    }
  }

  const handleLabelClick = (event: MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation()
    onStartEditing()
  }

  const handleLabelKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      onStartEditing()
    }
  }

  const handleInputBlur = () => {
    onCommit()
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      onCommit()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      onCancel()
    }
  }

  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'z-10' : undefined}>
      <div
        role="button"
        tabIndex={0}
        onClick={handleContainerClick}
        onKeyDown={handleContainerKeyDown}
        className={`w-full text-left rounded-md border px-4 py-3 transition-colors duration-150 ${
          isActive ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:border-primary-200'
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <div className="flex items-center justify-between">
              {isEditing ? (
                <input
                  ref={inputRef}
                  value={draftLabel}
                  onChange={(event) => onChangeDraft(event.target.value)}
                  onBlur={handleInputBlur}
                  onKeyDown={handleInputKeyDown}
                  disabled={saving}
                  className="w-full rounded border border-primary-300 bg-white px-2 py-1 text-sm text-gray-800 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="未命名欄位"
                />
              ) : (
                <span
                  className="text-sm font-medium text-gray-800"
                  onClick={handleLabelClick}
                  tabIndex={0}
                  onKeyDown={handleLabelKeyDown}
                >
                  {label}
                </span>
              )}
              <span className="text-xs text-gray-500">
                {fieldTypeLabels[field.type] || field.type}
              </span>
            </div>
            {field.description && (
              <p className="mt-1 text-xs text-gray-500 line-clamp-2">{field.description}</p>
            )}
            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-500">
              {field.isRequired && (
                <span className="rounded bg-red-100 px-2 py-0.5 text-red-600">必填</span>
              )}
              {field.isRepeatable && (
                <span className="rounded bg-blue-100 px-2 py-0.5 text-blue-600">可重複</span>
              )}
              {field.category && (
                <span className="rounded bg-gray-100 px-2 py-0.5">{field.category}</span>
              )}
              {field.subcategory && (
                <span className="rounded bg-gray-100 px-2 py-0.5">{field.subcategory}</span>
              )}
            </div>
            {field.options && field.options.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
                {field.options.slice(0, 3).map(option => (
                  <span
                    key={option.value}
                    className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600"
                  >
                    {option.label}
                  </span>
                ))}
                {field.options.length > 3 && (
                  <span className="text-gray-400">+{field.options.length - 3}</span>
                )}
              </div>
            )}
          </div>
          <div
            className={`flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-gray-50 text-xs ${dragHandleClass}`}
            {...dragHandleProps}
          >
            <span className="text-xs">↕</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FieldCanvas
